import React, { useEffect, useState } from "react";
import { PoliteStatusPortal } from "./PoliteStatusPortal";

interface PageTitleAnnouncerProps {
    title: string;
    isReady: boolean;
}

/**
 * Announces the settled per-page browser-tab title to screen readers (WCAG 2.4.2, DEV-97762).
 *
 * `document.title` is updated visually on every page, but screen readers only read the document
 * title on the initial full-page load and do not re-read it on SPA navigation. This pushes the
 * settled per-page title into a polite live region once the page is ready, so it is announced once
 * per page - on first load and on every navigation. Because it uses the client-computed title, it
 * also covers randomized-block surveys (where the server cannot know the first page).
 *
 * The server renders a neutral placeholder in `<title>` on load, so every page (including the
 * first) is announced here once it resolves.
 *
 * Gated on `isReady` so the live region speaks the settled title, not the intermediate placeholder.
 */
export function PageTitleAnnouncer({ title, isReady }: PageTitleAnnouncerProps) {
    const [announced, setAnnounced] = useState("");

    useEffect(() => {
        if (!isReady || !title) {
            return;
        }
        setAnnounced(title);
    }, [title, isReady]);

    return <PoliteStatusPortal className="page-title-sr-live-region">{announced}</PoliteStatusPortal>;
}
